import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { trackStudyAction } from '@/lib/analytics/viewer';
import { cancelConsultationCase } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, FileText, Loader2, MessageSquare, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isFinalConsultStatus, isOpenConsultStatus } from './reportGlyph';
/**
 * The teleconsultation half of a study card's Reports section: EVERY case for
 * the study, newest first, each with its status, the group that holds it and
 * the day it was submitted. Before this the card showed the latest case alone,
 * so a signed consult followed by a fresh request looked like a single
 * contradictory row.
 *
 * Two sources feed it. The study-list row carries the whole history
 * (`study.consultations`); the per-study case query (`latestCase`) carries the
 * full latest case the existing status / quick-look / amendment actions work
 * on. An older backend sends no list, so the latest case alone still renders.
 *
 * Any open case can be CLOSED from here: a clinic that had the read delivered
 * outside New Lumen (phone, PIMS, email) otherwise has an orange stethoscope
 * forever. Closing is the existing cancel endpoint, which the backend limits
 * to non-terminal cases and to submitting-clinic members holding
 * request_teleconsultation. Without it (View Only) the cases still list and
 * open, but amend, close and a fresh request are not offered.
 */
export function StudyConsultationsList({ study, latestCase, onOpenLatest, onAmendLatest, onSubmitNew, canRequest, }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [closing, setClosing] = useState(null);
    const rows = useMemo(() => mergeCases(study.consultations, latestCase), [study, latestCase]);
    const closeMutation = useMutation({
        mutationFn: (caseId) => cancelConsultationCase(caseId),
        onSuccess: () => {
            toast({ title: t('worklist.consultClosed') });
            setClosing(null);
            // The row's glyph and this list both read the study list, and the card's
            // actions read the per-study case: refresh both so nothing keeps
            // offering to close a case that is already closed.
            void queryClient.invalidateQueries({ queryKey: ['studies'] });
            void queryClient.invalidateQueries({ queryKey: ['study-consultation', study.id] });
        },
        onError: () => toast({ title: t('worklist.consultCloseFailed'), variant: 'destructive' }),
    });
    if (rows.length === 0)
        return null;
    return (_jsxs(_Fragment, { children: [rows.map((c) => {
                const isLatest = c.id === latestCase?.id;
                const isOpen = isOpenConsultStatus(c.status);
                const isFinal = isFinalConsultStatus(c.status);
                const submitted = formatSubmitted(c.submittedAt);
                const rowBody = (_jsxs(_Fragment, { children: [_jsx("span", { className: cn('inline-block shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase', CONSULT_STATUS_CHIP[c.status] ?? 'bg-zinc-500/15 text-zinc-400'), children: t('worklist.consultStatus', { status: consultStatusLabel(c.status) }) }), _jsx("span", { className: "text-foreground/90 truncate text-xs", "data-testid": `study-consultation-group-${c.id}`, children: c.groupName?.trim() || t('worklist.consultGroupUnknown') }), submitted && (_jsx("span", { className: "text-muted-foreground ml-auto shrink-0 text-[10px]", title: t('worklist.consultSubmitted', { date: submitted }), children: submitted })), isLatest &&
                            (isFinal ? (_jsx(FileText, { className: "text-muted-foreground h-3 w-3 shrink-0", "aria-hidden": true })) : (_jsx(MessageSquare, { className: "text-muted-foreground h-3 w-3 shrink-0", "aria-hidden": true })))] }));
                return (_jsxs("div", { className: "space-y-1 pointer-coarse:space-y-2", "data-testid": `study-consultation-${c.id}`, children: [isLatest ? (
                        // Only the latest case has its full record loaded, so only it
                        // opens the status view / quick-look; the history rows are
                        // reference, not targets.
                        _jsx("button", { type: "button", onClick: onOpenLatest, className: "flex w-full items-center gap-2 rounded px-1 py-0.5 text-left transition-colors hover:bg-white/5 pointer-coarse:py-2", title: isFinal
                                ? t('worklist.consultDialog.viewReport')
                                : t('worklist.consultDialog.title'), children: rowBody })) : (_jsx("div", { className: "flex w-full items-center gap-2 px-1 py-0.5", children: rowBody })), canRequest && isLatest && (c.status === 'pending' || c.status === 'claimed') && (_jsxs("button", { type: "button", onClick: onAmendLatest, className: "flex items-center gap-1.5 rounded px-1 py-0.5 text-xs text-amber-400 transition-colors hover:bg-amber-500/10 pointer-coarse:py-2", title: t('worklist.sendAmendmentTooltip'), children: [_jsx(Send, { className: "h-3 w-3" }), t('worklist.sendAmendment')] })), canRequest && isOpen && (_jsxs("button", { type: "button", onClick: () => setClosing(c), className: "flex items-center gap-1.5 rounded px-1 py-0.5 text-xs text-red-400 transition-colors hover:bg-red-500/10 pointer-coarse:py-2", title: t('worklist.closeConsultTooltip'), "data-testid": `study-consultation-close-${c.id}`, children: [_jsx(Ban, { className: "h-3 w-3" }), t('worklist.closeConsult')] })), canRequest && isLatest && c.status === 'cancelled' && (_jsxs("button", { type: "button", onClick: onSubmitNew, className: "flex items-center gap-1.5 rounded px-1 py-0.5 text-xs text-cyan-400 transition-colors hover:bg-cyan-500/10 pointer-coarse:py-2", children: [_jsx(MessageSquare, { className: "h-3 w-3" }), t('worklist.submitNewConsult')] }))] }, c.id));
            }), _jsx(Dialog, { open: closing !== null, onOpenChange: (open) => {
                    if (!open && !closeMutation.isPending)
                        setClosing(null);
                }, children: _jsxs(DialogContent, { className: "sm:max-w-md", "data-testid": "close-consultation-dialog", children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { className: "flex items-center gap-2", children: [_jsx(AlertTriangle, { className: "text-destructive h-5 w-5 shrink-0", "aria-hidden": true }), t('worklist.closeConsultDialog.title')] }), _jsx(DialogDescription, { children: t('worklist.closeConsultDialog.body') })] }), closing?.groupName?.trim() && (_jsx("p", { className: "text-muted-foreground text-xs", children: t('worklist.closeConsultDialog.group', { group: closing.groupName.trim() }) })), _jsxs(DialogFooter, { children: [_jsx(Button, { type: "button", variant: "outline", onClick: () => setClosing(null), disabled: closeMutation.isPending, children: t('worklist.closeConsultDialog.keep') }), _jsxs(Button, { type: "button", variant: "destructive", disabled: closeMutation.isPending, onClick: () => {
                                        if (!closing)
                                            return;
                                        trackStudyAction('study.consultation_closed');
                                        closeMutation.mutate(closing.id);
                                    }, "data-testid": "close-consultation-confirm", children: [closeMutation.isPending && _jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" }), t('worklist.closeConsultDialog.confirm')] })] })] }) })] }));
}
/**
 * The list the card shows. The study-list row is the primary source; the
 * latest case from the per-study query is folded in when the list does not
 * carry it yet (the two are fetched separately and one can lag the other
 * right after a submit). Deleted cases vanish from every read path.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function mergeCases(fromList, latestCase) {
    const rows = [...(fromList ?? [])];
    if (latestCase && !rows.some((c) => c.id === latestCase.id)) {
        rows.push({
            id: latestCase.id,
            status: latestCase.status,
            submittedAt: latestCase.submittedAt,
            groupName: latestCase.assignedGroupName ?? null,
        });
    }
    return rows
        .filter((c) => c.status !== 'deleted')
        .sort((a, b) => submittedMs(b.submittedAt) - submittedMs(a.submittedAt));
}
function submittedMs(iso) {
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? 0 : ms;
}
function formatSubmitted(iso) {
    return formatDate(iso, { month: 'short', day: 'numeric', year: 'numeric' }) || null;
}
/** Same wording the card has always used for the chip: signed states read as Final. */
function consultStatusLabel(status) {
    if (status === 'amended')
        return 'Final + Addendum';
    if (status === 'delivered')
        return 'Final';
    return status.replace(/_/g, ' ');
}
const CONSULT_STATUS_CHIP = {
    pending: 'bg-amber-500/15 text-amber-400',
    claimed: 'bg-blue-500/15 text-blue-400',
    in_progress: 'bg-cyan-500/15 text-cyan-400',
    preliminary: 'bg-blue-500/15 text-blue-400',
    final: 'bg-green-500/15 text-green-400',
    delivered: 'bg-green-500/15 text-green-400',
    amended: 'bg-purple-500/15 text-purple-400',
    cancelled: 'bg-zinc-500/15 text-zinc-400',
};
